// Pure layout compute for the DAG canvas renderer.
//
// Extracted from dag-graph.ts so the math (parent map, orphan partition,
// row-wrapped Sugiyama positioning, Zherebko offset / route rebase) can be
// tested without instantiating a canvas, gsap, ResizeObserver or LiveView
// hook. No DOM, no globals, no .dox import — the input is a plain edge/node
// list plus container width; the output is a positions map + transformed
// edge list ready for the canvas-mutation pass to consume verbatim.

import {
  buildDagGraph,
  dagLinks,
  dagNodes,
  layoutSugiyama,
  layoutZherebko,
  type DagGraph,
  type DagNode,
} from "./d3-dag-shim";

export type NodeId = string;

export interface LayoutNode {
  readonly id: NodeId;
}

export interface LayoutEdge {
  readonly source: NodeId;
  readonly target: NodeId;
}

/** Build upstream/downstream adjacency maps from an edge list. */
export const buildAdjacency = (
  edges: ReadonlyArray<LayoutEdge>,
): {
  readonly upstream: ReadonlyMap<NodeId, ReadonlySet<NodeId>>;
  readonly downstream: ReadonlyMap<NodeId, ReadonlySet<NodeId>>;
} => {
  const up = new Map<NodeId, Set<NodeId>>();
  const down = new Map<NodeId, Set<NodeId>>();
  for (const e of edges) {
    if (!up.has(e.target)) up.set(e.target, new Set());
    up.get(e.target)!.add(e.source);
    if (!down.has(e.source)) down.set(e.source, new Set());
    down.get(e.source)!.add(e.target);
  }
  return { upstream: up, downstream: down };
};

/** BFS over an adjacency map. Returns reachable ids EXCLUSIVE of `startId`. */
export const transitiveChain = (
  adj: ReadonlyMap<NodeId, ReadonlySet<NodeId>>,
  startId: NodeId,
): ReadonlySet<NodeId> => {
  const visited = new Set<NodeId>();
  const queue: NodeId[] = [startId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    const neighbors = adj.get(current);
    if (neighbors)
      for (const n of neighbors) if (!visited.has(n)) queue.push(n);
  }
  visited.delete(startId);
  return visited;
};

export interface LayoutPosition {
  readonly x: number;
  readonly y: number;
}

export interface LayoutDims {
  readonly nodeW: number;
  readonly nodeH: number;
  readonly nodeGapY: number;
  readonly margin: number;
}

export interface LayoutInput {
  readonly nodes: ReadonlyArray<LayoutNode>;
  readonly edges: ReadonlyArray<LayoutEdge>;
  readonly availableW: number;
  readonly dims: LayoutDims;
}

export interface LayoutResult {
  /** positions are post-margin (i.e. drawable canvas coords). */
  readonly positions: Map<NodeId, LayoutPosition>;
  /**
   * Routed edges in canvas coords. `points` is null for plain bezier edges
   * (Sugiyama mode) and a polyline-with-bezier-smoothing path for Zherebko.
   */
  readonly edges: ReadonlyArray<{
    readonly source: NodeId;
    readonly target: NodeId;
    readonly points: ReadonlyArray<readonly [number, number]> | null;
  }>;
  readonly width: number;
  readonly height: number;
  readonly mode: "sugiyama" | "zherebko";
  readonly maxPerRow: number;
}

/** Layout signature for cache-on-resize. */
export const layoutSignature = (
  availableW: number,
  dims: LayoutDims,
): string => {
  const slotW = dims.nodeW + 20;
  const maxPerRow = Math.max(1, Math.floor(availableW / slotW));
  return maxPerRow === 1 ? "zherebko" : `sugiyama:${maxPerRow}`;
};

/** Build parent-id list per node from the edge list. */
export const parentMap = (
  nodes: ReadonlyArray<LayoutNode>,
  edges: ReadonlyArray<LayoutEdge>,
): ReadonlyMap<NodeId, ReadonlyArray<NodeId>> => {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const acc = new Map<NodeId, NodeId[]>(nodes.map((n) => [n.id, []]));
  for (const e of edges) {
    if (nodeIds.has(e.source)) acc.get(e.target)?.push(e.source);
  }
  return acc;
};

/** Partition nodes into (orphan, connected) by membership in any edge. */
export const partitionOrphans = (
  nodes: ReadonlyArray<LayoutNode>,
  edges: ReadonlyArray<LayoutEdge>,
): {
  readonly orphans: ReadonlyArray<LayoutNode>;
  readonly connected: ReadonlyArray<LayoutNode>;
} => {
  const connectedIds = edges.reduce((acc, e) => {
    acc.add(e.source);
    acc.add(e.target);
    return acc;
  }, new Set());
  return {
    orphans: nodes.filter((n) => !connectedIds.has(n.id)),
    connected: nodes.filter((n) => connectedIds.has(n.id)),
  };
};

/**
 * Pure layout pipeline. Computes node positions and routed edge polylines
 * from a plain graph + container width, picking Zherebko vs row-wrapped
 * Sugiyama based on the slot count that fits within `availableW`.
 *
 * Side-effect-free: depends only on d3-dag, which is itself pure.
 */
export const computeLayout = (input: LayoutInput): LayoutResult => {
  const { nodes, edges, availableW, dims } = input;
  const { nodeW, nodeH, nodeGapY, margin } = dims;
  const slotW = nodeW + 20;
  const maxPerRow = Math.max(1, Math.floor(availableW / slotW));
  const useZherebko = maxPerRow === 1;
  const mode: "sugiyama" | "zherebko" = useZherebko ? "zherebko" : "sugiyama";

  const positions = new Map<NodeId, LayoutPosition>();
  const routedEdges: Array<{
    source: NodeId;
    target: NodeId;
    points: ReadonlyArray<readonly [number, number]> | null;
  }> = [];

  if (nodes.length === 0) {
    return {
      positions,
      edges: routedEdges,
      width: availableW + margin * 2,
      height: margin * 2,
      mode,
      maxPerRow,
    };
  }

  const parents = parentMap(nodes, edges);
  const { orphans, connected } = useZherebko
    ? partitionOrphans(nodes, edges)
    : { orphans: [], connected: nodes };

  const layoutNodes = useZherebko ? connected : nodes;
  const stratData = layoutNodes.map((n) => ({
    id: n.id,
    parentIds: parents.get(n.id) ?? [],
  }));

  let dag: DagGraph | null = null;
  if (stratData.length > 0) {
    const built = buildDagGraph(stratData);
    dag = useZherebko
      ? layoutZherebko(built, nodeW + 20, nodeH + nodeGapY)
      : layoutSugiyama(built, nodeW + 20, nodeH + nodeGapY);
  }

  let currentY = 0;
  const tx = (x: number) => x + margin;
  const ty = (y: number) => y + margin;

  if (useZherebko) {
    currentY = orphans.length * (nodeH + nodeGapY);
    if (dag) {
      const nodes = dagNodes(dag);
      const firstNode = nodes[0];
      const zherebkoNodeX = firstNode?.x ?? (nodeW + 20) / 2;
      const zherebkoTopMargin = (nodeH + nodeGapY) / 2;
      const yShift = currentY - zherebkoTopMargin;
      const targetX = availableW / 2;
      for (const n of nodes) {
        n.x = targetX;
        n.y += yShift;
      }
      for (const link of dagLinks(dag)) {
        routedEdges.push({
          source: link.source.data.id,
          target: link.target.data.id,
          points: link.points.map(
            ([px, py]) =>
              [targetX + (px - zherebkoNodeX), py + yShift] as const,
          ),
        });
      }
      let maxY = 0;
      for (const n of nodes) maxY = Math.max(maxY, n.y);
      currentY = maxY + nodeH / 2;
    }
  } else if (dag) {
    const layerMap = new Map<number, DagNode[]>();
    for (const n of dagNodes(dag)) {
      const ly = Math.round(n.y * 100) / 100;
      const bucket = layerMap.get(ly);
      if (bucket) bucket.push(n);
      else layerMap.set(ly, [n]);
    }
    const sortedLayers = [...layerMap.keys()].sort((a, b) => a - b);
    const rowH = nodeH + nodeGapY;
    for (const ly of sortedLayers) {
      const layer = layerMap.get(ly)!;
      layer.sort((a, b) => a.x - b.x);
      const rowCount = Math.ceil(layer.length / maxPerRow);
      for (let row = 0; row < rowCount; row++) {
        const start = row * maxPerRow;
        const end = Math.min(start + maxPerRow, layer.length);
        const count = end - start;
        const totalW = count * slotW;
        const x0 = (availableW - totalW) / 2 + slotW / 2;
        for (let i = start; i < end; i++) {
          const n = layer[i];
          if (!n) continue;
          n.x = x0 + (i - start) * slotW;
          n.y = currentY;
        }
        currentY += rowH;
      }
    }
    for (const link of dagLinks(dag)) {
      routedEdges.push({
        source: link.source.data.id,
        target: link.target.data.id,
        points: null,
      });
    }
  }

  // Orphans first
  const rowH = nodeH + nodeGapY;
  const centerX = availableW / 2;
  orphans.forEach((n, i) => {
    positions.set(n.id, { x: tx(centerX), y: ty(i * rowH) });
  });
  // Layout nodes from d3-dag
  if (dag) {
    for (const node of dagNodes(dag)) {
      positions.set(node.data.id, {
        x: tx(node.x),
        y: ty(node.y),
      });
    }
  }

  return {
    positions,
    edges: routedEdges,
    width: availableW + margin * 2,
    height: currentY + margin * 2,
    mode,
    maxPerRow,
  };
};

/**
 * Pure adjacency-driven highlight: the union of the start node, its
 * upstream-transitive set, and its downstream-transitive set. Used by the
 * hover layer to dim everything outside the chain.
 */
export const computeHoverHighlight = (
  adjacency: {
    readonly upstream: ReadonlyMap<NodeId, ReadonlySet<NodeId>>;
    readonly downstream: ReadonlyMap<NodeId, ReadonlySet<NodeId>>;
  },
  nodeId: NodeId,
): ReadonlySet<NodeId> => {
  const collect = (
    adj: ReadonlyMap<NodeId, ReadonlySet<NodeId>>,
  ): Set<NodeId> => {
    const visited = new Set<NodeId>();
    const queue: NodeId[] = [nodeId];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (visited.has(cur)) continue;
      visited.add(cur);
      const ns = adj.get(cur);
      if (ns) for (const n of ns) if (!visited.has(n)) queue.push(n);
    }
    visited.delete(nodeId);
    return visited;
  };
  return new Set<NodeId>([
    nodeId,
    ...collect(adjacency.upstream),
    ...collect(adjacency.downstream),
  ]);
};
